import { useEffect, useRef, memo } from "react";
import CountUp from "react-countup";
import { motion, AnimatePresence } from "framer-motion";

interface AnimatedValueProps {
  value: number;
  postUuid: string;
  field: string;
}

const AnimatedValue = memo(({ value, postUuid, field }: AnimatedValueProps) => {
  const prevValue = useRef(value);
  const initialRender = useRef(true);
  const hasChanged = value !== prevValue.current;

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      prevValue.current = value;
      return;
    }

    if (hasChanged) {
      prevValue.current = value;
    }
  }, [value, hasChanged]);

  const shouldAnimate = !initialRender.current && hasChanged;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={`${value}-${shouldAnimate}`}
        initial={shouldAnimate ? { scale: 0.95, opacity: 0.75 } : false}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 700,
          damping: 30,
          mass: 0.5,
          duration: 0.5,
        }}
      >
        <CountUp
          start={shouldAnimate ? prevValue.current : value}
          end={value}
          separator=","
          decimals={field === "engagement_rate" ? 2 : 0}
          duration={shouldAnimate ? 0.5 : 0}
          preserveValue={true}
        />
      </motion.div>
    </AnimatePresence>
  );
});

AnimatedValue.displayName = "AnimatedValue";

export { AnimatedValue };
